import { capitalize, compact, isString, map } from "lodash";
import React from "react";
import { AssetSummary } from "types/api/assetSummary";
import { FilterValue } from "types/pagination";
import { CellRenderers } from "utils/tableUtils";
import { extractTagValue } from "views/Findings/utils";

import { ACTableProps } from "ac-components/ACTable/ACTable";
import { RenderMethodType } from "ac-components/ac-multi-grouping-table/ACMultiGroupingTable";
import ACResizableColumnCell from "ac-components/ac-resizable-column-cell/ACResizableColumnCell";
import ACTag from "ac-components/ac-tag/ACTag";

export const CommonTableProps: ACTableProps<AssetSummary> = {
  rowKey: "id",
  sticky: true,
  autoEnableScroll: true,
  computeTableWidth: true,
  components: {
    header: {
      cell: ACResizableColumnCell,
    },
  },
};

export const DEFAULT_HIDDEN_COLUMN_KEYS = ["os", "owner"];

export enum ASSET_TYPE_VALUES {
  HOST = "HOST",
  IMAGE = "IMAGE",
  CLOUD_RESOURCE = "CLOUD_RESOURCE",
  REPOSITORY = "REPOSITORY",
  NETWORK_RESOURCE = "NETWORK_RESOURCE",
  OTHERS = "OTHERS",
}

export const ASSET_TYPE_FIELD_DISPLAY_MAP: { [key: string]: string } = {
  [ASSET_TYPE_VALUES.HOST]: "Host",
  [ASSET_TYPE_VALUES.IMAGE]: "Image",
  [ASSET_TYPE_VALUES.CLOUD_RESOURCE]: "Cloud Resource",
  [ASSET_TYPE_VALUES.REPOSITORY]: "Repository",
  [ASSET_TYPE_VALUES.OTHERS]: "Others",
  [ASSET_TYPE_VALUES.NETWORK_RESOURCE]: "Network Resource",
};

export const getCustomColumnTitles = () => {
  return {
    cloudProvider: "Cloud Provider",
    cloudAccountId: "Cloud Account ID",
    location: "Location",
  };
};
export const DEFAULT_DATE_FORMATS = {
  firstSeen: "date",
};

export const DATE_FILTER_API_SUPPORT = ["firstSeen"];

export const GroupByFieldRender = (field: string): RenderMethodType => {
  return (record) => {
    if (field.startsWith("keyValue.")) {
      return CellRenderers.VALUE_OR_NA(extractTagValue(record.name));
    }
    switch (field) {
      case "active":
        return record.name === "true" ? "Active" : "Inactive";
      case "armorCodeType":
        return (
          <ACTag
            label={
              ASSET_TYPE_FIELD_DISPLAY_MAP[record.name] ??
              capitalize(record.name)
            }
            mode="label"
          />
        );
      default:
        return record.displayName;
    }
  };
};

export const convertAssetAggField = (key: string) => {
  if (key.startsWith("keyValue.")) {
    return "tags";
  }
  return key;
};

export const getIncludeAssetKeysField = (keys: string[]) => {
  return compact(
    map(keys, (key) => {
      if (key.startsWith("keyValue.")) {
        return key.replace("keyValue.", "");
      }
      return "";
    }),
  );
};

export const convertAssetFilterField = (key: string, value?: FilterValue) => {
  if (key === "tags") {
    if (isString(value)) {
      return `keyValue.${value.split(":")[0]}`;
    }
    return "keyValue";
  }

  return key;
};

export const getAssetFieldValue = (
  fieldName: string,
  fieldValue: FilterValue,
): FilterValue[] => {
  if (fieldValue === "null" || fieldValue === null) {
    return [null];
  }
  if (fieldName.startsWith("keyValue.") && isString(fieldValue)) {
    return [
      // exact match filter
      `"${extractTagValue(fieldValue)}"`,
    ];
  }

  if (["tags", "keyValue"].includes(fieldName) && isString(fieldValue)) {
    const [, ...valueParts] = fieldValue.split(":");
    return compact([
      valueParts.length ? `"${valueParts.join(":")}"` : undefined,
    ]);
  }

  return [fieldValue];
};

export const IPv4FilterMessage = (
  <>
    <span>
      {" "}
      Displays the asset's IPv4 address (e.g., 192.168.0.1) and supports
      filtering by single IP, multiple IPs, or IP ranges.
    </span>
    <br />
    <br />
    <span>Single: 10.0.0.1</span>
    <br />
    <span>Mutiple: 10.0.0.2,10.0.0.7,10.0.0.9</span>
    <br />
    <span>Range: 10.0.0.1-10.0.0.20</span>
    <br />
    <span>Range: 10.0.0.1-10.0.0.20,100.0.10.1-100.0.10.150</span>
  </>
);

export const IPv6FilterMessage = (
  <>
    <span>
      {" "}
      Displays the asset's IPv6 address (e.g., 2001:db8::1) and supports
      filtering by single IP, multiple IPs, or IP ranges.
    </span>
    <br />
    <br />
    <span>Single: 2001:0db8::1</span>
    <br />
    <span>Mutiple: 2001:0db8::1,2001:0db8::2</span>
    <br />
    <span>Range: 2001:0db8::1-2001:0db8::ffff</span>
    <br />
    <span>
      Multiple Range: 2001:0db8::1-2001:0db8::ffff, 2001:0db9::1-2001:0db9::abcd
    </span>
  </>
);

export const ALL_ASSET_COLUMNS: Record<string, string> = {
  armorCodeType: "Type",
  cloudProvider: "Cloud Provider",
  cloudAccountId: "Cloud Account ID",
  location: "Location",
  os: "OS",
  osVersion: "OS Version",
  source: "Source",
  active: "Status",
  owner: "Asset Owner",
};

export const GroupBySupportedFields = Object.keys(ALL_ASSET_COLUMNS);
